import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import Log from 'utils/log';
import t from 'utils/translate';
import LocalEvent from 'utils/local-event';
import { SoundDeviceType } from 'constants/sound-settings-constants';
import SnackbarTypes from 'constants/snackbar-types';
import { addSnackbar } from 'utils/dialogs-manager';
import { execBackgroundTask } from '../../../utils/functions';

const defaultId = 'default';

const initialState = {
    devicesDataLoaded: false,
    /**
     * The list of INPUT media devices sorted by label.
     * @type MediaDeviceInfo[]
     */
    inputDevicesList: [],
    /**
     * The list of OUTPUT media devices sorted by label.
     * @type MediaDeviceInfo[]
     */
    outputDevicesList: [],
    // by default will mark as used the devices labeled with 'default'
    activeInputDeviceId: defaultId,
    // by default will mark as used the devices labeled with 'default'
    activeOutputDeviceId: defaultId,
    soundSettingsPopupOn: false,
};

class SoundSettingsSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!SoundSettingsSliceHandler.instance) {
            SoundSettingsSliceHandler.instance = new SoundSettingsSliceHandler();
        }

        return SoundSettingsSliceHandler.instance;
    }

    constructor() {
        super('soundSettingsSlice');

        this.usedDeviceIdList = [];

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                setDevicesDataLoaded(state, action) {
                    state.devicesDataLoaded = action.payload;
                },
                setSoundSettingsPopup(state, action) {
                    state.soundSettingsPopupOn = action.payload;
                },
                setInputDevicesList(state, action) {
                    state.inputDevicesList = action.payload;
                },
                setOutputDevicesList(state, action) {
                    state.outputDevicesList = action.payload;
                },
                setActiveInputDeviceId(state, action) {
                    const current = state.activeInputDeviceId;

                    if (action.payload === current) {
                        return;
                    }

                    state.activeInputDeviceId = action.payload;

                    LocalEvent.trigger(LocalEvent.eventTypes.AUDIO_CONFIGURATION_INPUT_UPDATED, {
                        oldDeviceId: current,
                        newDeviceId: action.payload,
                    });
                },
                setActiveOutputDeviceId(state, action) {
                    const current = state.activeOutputDeviceId;

                    if (action.payload === current) {
                        return;
                    }

                    state.activeOutputDeviceId = action.payload;

                    LocalEvent.trigger(LocalEvent.eventTypes.AUDIO_CONFIGURATION_OUTPUT_UPDATED, {
                        oldDeviceId: current,
                        newDeviceId: action.payload,
                    });
                },
            },
        });
    
        LocalEvent.setListener(LocalEvent.eventTypes.LAYOUT_MANAGER_INITIAL_SCRIPTS_LOADED, (e) => {
            this.loadAvailableDevices(false).then(
                (success) => success && Log.debug('SOUND SETTINGS STORE', `Loaded devices list for the first time.`)
            );
        });
        
        // listen for changes in the list of plugged in devices
        navigator.mediaDevices.addEventListener('devicechange', () => {
            this.loadAvailableDevices(true).then(
                (success) => success && Log.debug('SOUND SETTINGS STORE', `Refreshed devices list upon changes at OS level.`)
            );
        });
    }

    /**
     * Load under the store all devices published in the browser.
     * @return Promise<boolean>
     */
    loadAvailableDevices = (forRefresh = false) =>
        new Promise((resolve) => {
    
            if (!navigator.mediaDevices.enumerateDevices) {
                
                Log.error('SOUND SETTINGS STORE', 'enumerateDevices() not supported - cannot retrieve devices list.');
        
                resolve(false);
    
                this.dispatch('setDevicesDataLoaded', true);
                
                return;
            }
    
            this.dispatch('setDevicesDataLoaded', false);
            
            // load the list of available devices and ask user permission to use them
            navigator.mediaDevices
            .getUserMedia({ video: false, audio: true })
            .then((stream) => {

                const state = this.getOwnState();
    
                const currentInputs = state.inputDevicesList;
                const currentOutputs = state.outputDevicesList;
                const inputs = [];
                const outputs = [];
    
                let addedInputDevice = null;
                let addedOutputDevice = null;
    
                navigator.mediaDevices
                .enumerateDevices()
                .then((devices) => {
                    devices.forEach(
                        (device) => {
                            if (![SoundDeviceType.INPUT, SoundDeviceType.OUTPUT].includes(device.kind)) {
                                // we care only about inputs and outputs
                                return;
                            }
                
                            // treat input case
                            if (device.kind === SoundDeviceType.INPUT) {
                    
                                if (
                                    forRefresh
                                    && currentInputs.filter(
                                        (currentInput) => currentInput.deviceId === device.deviceId
                                    ).length === 0
                                ) {
                                    Log.debug(
                                        'SOUND SETTINGS STORE',
                                        `The INPUT device with id ${device.deviceId} (${device.label}) has be plugged in`,
                                    );
                        
                                    addedInputDevice = device;
                                }
                    
                                inputs.push(device);
                    
                                return;
                            }
                
                            // treat output case
                            if (
                                forRefresh
                                && currentOutputs.filter(
                                    (currentOutput) => currentOutput.deviceId === device.deviceId
                                ).length === 0
                            ) {
                                Log.debug(
                                    'SOUND SETTINGS STORE',
                                    `The OUTPUT device with id ${device.deviceId} (${device.label}) has be plugged in`,
                                );
                    
                                addedOutputDevice = device;
                            }
                
                            outputs.push(device);
                        });
        
                    this.dispatch('setInputDevicesList', inputs);
                    this.dispatch('setOutputDevicesList', outputs);
    
                    Log.debug(
                        'SOUND SETTINGS STORE',
                        `Found ${inputs.length} INPUT devices and ${outputs.length} OUTPUT devices`,
                    );
                    
                    if (forRefresh === false) {
                    
                        this.setActiveInputId(defaultId, false);
                        this.setActiveOutputId(defaultId, false);
                        
                    } else {
                        // switch to the new microphone, if any
                        if (addedInputDevice !== null) {
        
                            Log.notice(
                                'SOUND SETTINGS STORE',
                                `Automatic switch to new INPUT device ${addedInputDevice.deviceId}(${addedInputDevice.label}`
                            );
        
                            this.setActiveInputId(addedInputDevice.deviceId);
                        }
    
                        // check if the current active output has been removed
                        if (
                            state.activeOutputDeviceId !== defaultId
                            && outputs.filter((output) => output.deviceId === state.activeOutputDeviceId).length === 0
                        ) {
                            Log.notice(
                                'SOUND SETTINGS STORE',
                                `The active OUTPUT device with id ${state.activeInputDeviceId} has been unplugged, `
                                + ` will switch to default.`
                            );
        
                            this.setActiveOutputId(defaultId, false);
                        }
                    }
    
                    this.dispatch('setDevicesDataLoaded', true);
                    resolve(true);
                })
                .catch((err) => {
                    Log.error(
                        'SOUND SETTINGS STORE',
                        `There was an error while loading the available devices list ${err}.`,
                    );
                    this.dispatch('setDevicesDataLoaded', true);
                    resolve(false);
                    
                });

            })
            .catch((err) => {
                Log.error('AudioConfigurationManager', `Error getting access to user media devices: ${err.message}`);
                this.dispatch('setDevicesDataLoaded', true);
                resolve(false);
            });
        });

    /**
     * Change the INPUT device used.
     * According with https://stackoverflow.com/questions/29051143/is-there-a-way-to-set-default-capture-device-in-chrome-like-navigator-setusermed
     * to change the input device is enough to select it with all properties set, including the device id.
     * @param {string} deviceId
     * @param {boolean} showSnackbar
     */
    setActiveInputId = (deviceId, showSnackbar = true) => {
        // ask for permission to use the selected input
        navigator.mediaDevices
            .getUserMedia({
                video: false,
                audio: {
                    autoGainControl: true,
                    deviceId,
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            })
            .then(() => {
                Log.debug('SOUND SETTINGS STORE', `Audio input set to use device id = ${deviceId}`);

                this.dispatch('setActiveInputDeviceId', deviceId);

                if (showSnackbar) {
                    addSnackbar({
                        type: SnackbarTypes.SUCCESS,
                        title: t('menu_sound_settings'),
                        message: t('js_all_sound_settings_changed_successfully'),
                    });
                }
            })
            .catch((error) => {
                Log.error('SOUND SETTINGS STORE', `Failed to set the selected input device. Error: ${error.message}`);

                addSnackbar({
                    type: SnackbarTypes.ERROR,
                    title: t('menu_sound_settings'),
                    message: t('global_unexpected_error'),
                });
            });
    };

    /**
     * @param {string} deviceId
     * @param {boolean} showSnackbar
     */
    setActiveOutputId = (deviceId, showSnackbar = true) => {
        this.dispatch('setActiveOutputDeviceId', deviceId);

        if (showSnackbar) {
            addSnackbar({
                type: SnackbarTypes.SUCCESS,
                title: t('menu_sound_settings'),
                message: t('js_all_sound_settings_changed_successfully'),
            });
        }

        // propagate the device change in the existing media elements
        execBackgroundTask(() => this.applyOutputDeviceChangesInDom());
    };

    /**
     * Controls the SoundSettings popup visibility
     *
     * @param state boolean
     */
    setSoundSettingsPopup = (state) => {
        this.dispatch('setSoundSettingsPopup', state);
    };

    applyOutputDeviceChangesInDom = async () => {
        const outputDeviceId = this.getOwnState().activeOutputDeviceId;

        if (!('HTMLMediaElement' in window) || !('setSinkId' in window.HTMLMediaElement.prototype)) {
            return;
        }

        const promises = [];

        // Need to check for audio HTML elements inside the document and this is outside the React scope
        const audioElements = document.querySelectorAll('audio');

        if (audioElements.length === 0) {
            Log.notice(
                'SOUND SETTINGS STORE',
                `There are no audio elements that should use the new output ${outputDeviceId}`,
            );

            return;
        }

        audioElements.forEach((audioElement) => {
            promises.push(this.setAudioOutputOnElement(audioElement));
        });

        await Promise.all(promises).then(() => {
            Log.debug(
                'SOUND SETTINGS STORE',
                `Applied the output device id ${outputDeviceId} on ${audioElements.length} audio elements`,
            );
        });
    };

    /**
     * Set the audio output device used by audio element
     *
     * @param element HTMLAudioElement
     * @returns Promise
     */
    setAudioOutputOnElement = async (element) => {
        const deviceId = this.getOwnState().activeOutputDeviceId;

        try {
            await element.setSinkId(deviceId);
        } catch (error) {
            Log.error('SOUND SETTINGS STORE', `Could not set the audio output device. Error ${error.message}`);
        }
    };
    /**
     * Refresh the list of available devices
     * when the user clicks the Refresh list button
     */
    refreshDevicesData = () => {
        this.loadAvailableDevices(true).then(
            (success) => success && Log.debug('SOUND SETTINGS STORE', 'Refreshed devices list on user request.')
        );
    };
}

export const soundSettingsSliceHandler = SoundSettingsSliceHandler.getInstance();
export const selectDevicesDataLoaded = (state) => state.soundSettingsSlice.devicesDataLoaded;
export const selectInputDevices = (state) => state.soundSettingsSlice.inputDevicesList;
export const selectOutputDevices = (state) => state.soundSettingsSlice.outputDevicesList;
export const selectActiveInputDeviceId = (state) => state.soundSettingsSlice.activeInputDeviceId;
export const selectActiveOutputDeviceId = (state) => state.soundSettingsSlice.activeOutputDeviceId;
export const selectSoundSettingsPopupOn = (state) => state.soundSettingsSlice.soundSettingsPopupOn;
export const {
    refreshDevicesData,
    setActiveInputId,
    setActiveOutputId,
    setSoundSettingsPopup,
    applyOutputDeviceChangesInDom,
} = soundSettingsSliceHandler;
